import { Trans, useLingui } from '@lingui/react/macro'
import { Alert, Button, InputNumber, Modal, Space, Switch } from 'antd'
import { useRolodexCache } from 'features/rolodex'
import { Settings } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  EnhancedSensorEntry,
  RolodexEntryType,
  SensorThresholdConfig,
} from 'types'

const SensorCard = styled.div`
  background: ${({ theme }) => theme.forecast?.background3 || '#2a2a2a'};
  border: 1px solid ${({ theme }) => theme.forecast?.border || '#333'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`

const SensorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`

const SensorName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast?.activeText || '#fff'};
`

const ThresholdInputsContainer = styled.div<{ $enabled: boolean }>`
  opacity: ${({ $enabled }) => ($enabled ? 1 : 0.5)};
  transition: opacity 0.2s ease;
`

const ThresholdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`

const ThresholdLabel = styled.span`
  min-width: 80px;
  font-size: 12px;
  color: ${({ theme }) => theme.forecast?.defaultText || '#888'};
`

interface ThresholdConfigurationModalProps {
  open: boolean
  onClose: () => void
  sensors: EnhancedSensorEntry[]
  onSave: (thresholdConfigs: SensorThresholdConfig[]) => void
}

export const ThresholdConfigurationModal: React.FC<
  ThresholdConfigurationModalProps
> = ({ open, onClose, sensors, onSave }) => {
  const { t } = useLingui()

  const { patchEntry } = useRolodexCache()
  const [loading, setLoading] = useState(false)
  const [thresholdConfigs, setThresholdConfigs] = useState<
    SensorThresholdConfig[]
  >([])

  // Initialize threshold configurations from sensor data
  useEffect(() => {
    if (sensors.length > 0) {
      const configs = sensors.map((sensor) => {
        const thresholds = sensor.data.thresholds
        return {
          sensorId: sensor.id,
          enabled: thresholds?.enabled ?? false,
          lowerLowerThreshold: thresholds?.lowerLowerThreshold ?? null,
          lowerThreshold: thresholds?.lowerThreshold ?? null,
          upperThreshold: thresholds?.upperThreshold ?? null,
          upperUpperThreshold: thresholds?.upperUpperThreshold ?? null,
        }
      })
      setThresholdConfigs(configs)
    }
  }, [sensors])

  const updateThresholdConfig = (
    sensorId: string,
    updates: Partial<SensorThresholdConfig>
  ) => {
    setThresholdConfigs((prev) =>
      prev.map((config) =>
        config.sensorId === sensorId ? { ...config, ...updates } : config
      )
    )
  }

  const getSensorName = (sensor: EnhancedSensorEntry) => {
    const data = sensor.data
    return (data?.name as string) || sensor.id
  }

  const validateThresholds = (config: SensorThresholdConfig): string | null => {
    if (!config.enabled) return null

    // At least one threshold must be configured
    const configuredThresholds = [
      config.lowerLowerThreshold,
      config.lowerThreshold,
      config.upperThreshold,
      config.upperUpperThreshold,
    ].filter((threshold) => threshold !== null)

    if (configuredThresholds.length === 0) {
      return t`At least one threshold must be configured when enabled`
    }

    // Validate threshold ordering
    const thresholdPairs = [
      { value: config.lowerLowerThreshold, name: 'Lower Low' },
      { value: config.lowerThreshold, name: 'Lower' },
      { value: config.upperThreshold, name: 'Upper' },
      { value: config.upperUpperThreshold, name: 'Upper High' },
    ].filter((pair) => pair.value !== null)

    for (let i = 0; i < thresholdPairs.length - 1; i++) {
      if (thresholdPairs[i].value! >= thresholdPairs[i + 1].value!) {
        return t`${{ value131_20: thresholdPairs[i].name }} (${{ value131_47: thresholdPairs[i].value }}) must be less than ${{ value131_93: thresholdPairs[i + 1].name }} (${{ value131_124: thresholdPairs[i + 1].value }})`
      }
    }

    return null
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      // Validate all configurations
      const validationErrors: string[] = []
      thresholdConfigs.forEach((config) => {
        const error = validateThresholds(config)
        if (error) {
          const sensor = sensors.find((s) => s.id === config.sensorId)
          const sensorName = sensor ? getSensorName(sensor) : config.sensorId
          validationErrors.push(`${sensorName}: ${error}`)
        }
      })

      if (validationErrors.length > 0) {
        Modal.error({
          title: t`Validation Errors`,
          content: (
            <div>
              <p>
                <Trans>Please fix the following issues:</Trans>
              </p>
              <ul>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          ),
        })
        return
      }

      // Save threshold configurations to Rolodex entries
      for (const config of thresholdConfigs) {
        const thresholdData = config.enabled
          ? {
              enabled: true,
              ...(config.lowerLowerThreshold !== null && {
                lowerLowerThreshold: config.lowerLowerThreshold,
              }),
              ...(config.lowerThreshold !== null && {
                lowerThreshold: config.lowerThreshold,
              }),
              ...(config.upperThreshold !== null && {
                upperThreshold: config.upperThreshold,
              }),
              ...(config.upperUpperThreshold !== null && {
                upperUpperThreshold: config.upperUpperThreshold,
              }),
            }
          : {
              enabled: false,
            }

        patchEntry({
          id: config.sensorId,
          type: RolodexEntryType.DATA_POINTS,
          data: {
            thresholds: thresholdData,
          },
        })
      }

      // Call onSave with the threshold configurations
      onSave(thresholdConfigs)
      onClose()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={
        <Space>
          <Settings size={16} />
          <Trans>Configure Sensor Thresholds</Trans>
        </Space>
      }
      open={open}
      onCancel={onClose}
      zIndex={1040}
      width={700}
      footer={[
        <Button key='cancel' onClick={onClose}>
          <Trans>Cancel</Trans>
        </Button>,
        <Button
          key='save'
          type='primary'
          loading={loading}
          onClick={handleSave}>
          <Trans>Save Configurations</Trans>
        </Button>,
      ]}>
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {sensors.length === 0 ? (
          <Alert
            title={t`No sensors selected`}
            description={t`Please select sensors from the sidebar to configure their thresholds.`}
            type='info'
            showIcon
          />
        ) : (
          <>
            <Alert
              title={t`Threshold Configuration`}
              description={t`Configure thresholds for each sensor. You can set any combination of Upper High, Upper, Lower, and Lower Low thresholds. At least one threshold must be configured when enabled. These will be displayed as reference lines on the chart when the Alert Thresholds layer is enabled.`}
              type='info'
              showIcon
              style={{ marginBottom: 16 }}
            />

            {sensors.map((sensor) => {
              const config = thresholdConfigs.find(
                (c) => c.sensorId === sensor.id
              )
              if (!config) return null

              const validationError = validateThresholds(config)

              return (
                <SensorCard key={sensor.id}>
                  <SensorHeader>
                    <SensorName>
                      <Settings size={16} />
                      {getSensorName(sensor)}
                    </SensorName>
                    <Switch
                      checked={config.enabled}
                      onChange={(enabled) =>
                        updateThresholdConfig(sensor.id, { enabled })
                      }
                      checkedChildren={t`Enabled`}
                      unCheckedChildren={t`Disabled`}
                    />
                  </SensorHeader>

                  <ThresholdInputsContainer $enabled={config.enabled}>
                    <ThresholdRow>
                      <ThresholdLabel>
                        <Trans>Upper High:</Trans>
                      </ThresholdLabel>
                      <InputNumber
                        value={config.upperUpperThreshold}
                        onChange={(value) =>
                          updateThresholdConfig(sensor.id, {
                            upperUpperThreshold: value,
                          })
                        }
                        placeholder={t`Enter upper high threshold`}
                        disabled={!config.enabled}
                        style={{ flex: 1 }}
                        step={0.1}
                        precision={2}
                      />
                    </ThresholdRow>

                    <ThresholdRow>
                      <ThresholdLabel>
                        <Trans>Upper:</Trans>
                      </ThresholdLabel>
                      <InputNumber
                        value={config.upperThreshold}
                        onChange={(value) =>
                          updateThresholdConfig(sensor.id, {
                            upperThreshold: value,
                          })
                        }
                        placeholder={t`Enter upper threshold`}
                        disabled={!config.enabled}
                        style={{ flex: 1 }}
                        step={0.1}
                        precision={2}
                      />
                    </ThresholdRow>

                    <ThresholdRow>
                      <ThresholdLabel>
                        <Trans>Lower:</Trans>
                      </ThresholdLabel>
                      <InputNumber
                        value={config.lowerThreshold}
                        onChange={(value) =>
                          updateThresholdConfig(sensor.id, {
                            lowerThreshold: value,
                          })
                        }
                        placeholder={t`Enter lower threshold`}
                        disabled={!config.enabled}
                        style={{ flex: 1 }}
                        step={0.1}
                        precision={2}
                      />
                    </ThresholdRow>

                    <ThresholdRow>
                      <ThresholdLabel>
                        <Trans>Lower Low:</Trans>
                      </ThresholdLabel>
                      <InputNumber
                        value={config.lowerLowerThreshold}
                        onChange={(value) =>
                          updateThresholdConfig(sensor.id, {
                            lowerLowerThreshold: value,
                          })
                        }
                        placeholder={t`Enter lower low threshold`}
                        disabled={!config.enabled}
                        style={{ flex: 1 }}
                        step={0.1}
                        precision={2}
                      />
                    </ThresholdRow>

                    {validationError && (
                      <Alert
                        title={validationError}
                        type='error'
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </ThresholdInputsContainer>
                </SensorCard>
              )
            })}
          </>
        )}
      </div>
    </Modal>
  )
}
